import _ from "lodash";
import { DBMappingInfo } from "../../framework.core/data/DBMappingInfo";
import { SQLField, SQLInfo, SQLType } from "../../framework.core/data/SQLInfo";
import { ChangedProperty, ChangeTrackable, CollectionChangedItem, CollectionChangedProperty, ComplexChangedProperty, TrackableEntity } from "./ChangeTrackable";
import { stringsEqualIgnoreCase } from "../../util";

export interface IDictionary<T> {
  [key:string]: T
}

export function getSQLInfoList2(ct: ChangeTrackable | null, dicMappings: IDictionary<DBMappingInfo>, sqlType:SQLType = SQLType.InsertOrUpdate, pKey: SQLField|undefined = undefined): SQLInfo[] {
  const sqlList:SQLInfo[] = [];

  if(ct === null) return sqlList;
  let key: SQLField|undefined = undefined;
  let keyTable = '';
  let keyMappingInfo: DBMappingInfo|null = null;
  if(pKey) {
    keyMappingInfo = dicMappings[_.lowerFirst(pKey.propertyName)];  // Ensure camelCase
    if(keyMappingInfo) {
      keyTable = keyMappingInfo.dbTable;
      key = pKey;
      if(!key.fieldName) key.fieldName = keyMappingInfo.dbField;
    }
  }

  // Delete
  if(sqlType === SQLType.Delete && key != null) {
    keyMappingInfo = dicMappings[_.lowerFirst(key.propertyName)]; // camelCase
    if(keyMappingInfo) {
      sqlList.push(new SQLInfo({
        tableName: keyMappingInfo.dbTable,
        type: SQLType.Delete,
        whereFields: _.cloneDeep(keyMappingInfo.dbParams),
        key: key
      }));
    }
  } else {
    // Add or change
    /// Primitives
    ct.changedProps.forEach(x => {
      keyMappingInfo = dicMappings[_.lowerFirst(x.propertyName)];   // camelCase
      if(keyMappingInfo) {
        if(stringsEqualIgnoreCase(keyMappingInfo.dbTable, keyTable)) key = pKey;
        else key = undefined;

        if(!key || !stringsEqualIgnoreCase(x.propertyName, key.propertyName)) {
          let sqlInfo = sqlList.find(w => stringsEqualIgnoreCase(w.tableName, keyMappingInfo?.dbTable) && 
            SQLFieldListEquals(w.whereFields, keyMappingInfo?.dbParams??[]) && w.key === key);
          if(!sqlInfo) {
            sqlInfo = new SQLInfo( {
              tableName: keyMappingInfo.dbTable,
              type: sqlType,
              whereFields: _.cloneDeep(keyMappingInfo.dbParams),
              key: key
            });
            sqlList.push(sqlInfo);
          }
          sqlInfo.fields.push(new SQLField({
            fieldName: keyMappingInfo.dbField,
            propertyName: _.upperFirst(x.propertyName),   // PascalCase
            value: x.newValue
          }));
        }
      }
    });

    /// Complex object
    ct.complexChangedProps.forEach(x => {      
      if(x.complexItem === null || !x.complexItem.keyPropertyName) return;
      keyMappingInfo = dicMappings[_.lowerFirst(x.complexItem.keyPropertyName)];    // camelCase
      if(keyMappingInfo) {
        sqlList.push(...getSQLInfoList2(x.complexItem.changeTrackable, 
          dicMappings,  // top Entity's dicMapping
          SQLType.InsertOrUpdate, 
          new SQLField({
            fieldName: keyMappingInfo.dbField,
            propertyName: _.upperFirst(x.complexItem.keyPropertyName),  // PascalCase
            value: x.complexItem.keyValue
          })
        ));
      }
    });

    /// object Collection
    ct.changedCollectionProps.forEach(x => {
      // insert
      x.addedItems.forEach(t => {
        if(!t.keyPropertyName) return;
        keyMappingInfo = dicMappings[_.lowerFirst(t.keyPropertyName)];  // camelCase
        if(keyMappingInfo) {
          sqlList.push(...getSQLInfoList2(t.changeTrackable, 
            dicMappings,  // top Entity's dicMapping
            SQLType.Insert, 
            new SQLField({
              fieldName: keyMappingInfo.dbField,
              propertyName: _.upperFirst(t.keyPropertyName),    // PascalCase
              value: t.keyValue
            })
          ));
        }
      });

      // update
      x.changedItems.forEach(t => {
        if(!t.keyPropertyName) return;
        keyMappingInfo = dicMappings[_.lowerFirst(t.keyPropertyName)];  // camelCase
        if(keyMappingInfo) {
          sqlList.push(...getSQLInfoList2(t.changeTrackable, 
            dicMappings, 
            SQLType.Update, 
            new SQLField({
              fieldName: keyMappingInfo.dbField,
              propertyName: _.upperFirst(t.keyPropertyName),  // PascalCase
              value: t.keyValue
            })
          ));
        }
      });

      // delete
      x.deletedItems.forEach(t => {
        if(!t.keyPropertyName) return;
        keyMappingInfo = dicMappings[_.lowerFirst(t.keyPropertyName)];  // camelCase
        if(keyMappingInfo) {
          sqlList.push(...getSQLInfoList2(t.changeTrackable, dicMappings, SQLType.Delete, new SQLField({
            fieldName: keyMappingInfo.dbField,
            propertyName: _.upperFirst(t.keyPropertyName),  // PascalCase
            value: t.keyValue
          })));
        }
      });
    });
  }
  return sqlList;
}

/**
 * @param te 
 */
export function getSQLInfoList<T extends TrackableEntity>(te:T): SQLInfo[] {
  let sqlType: SQLType|undefined = undefined;
  let keyField: SQLField|undefined = undefined;

  if(te.key) {
    let hasKey = false;
    let keyVal = (te as any)[te.key];
    if(typeof keyVal === 'number') hasKey = keyVal > 0;
    else {
      // key type is not number (In this case, key must not included in noTrackProps array)
      const cp = te.changedProps.find(x => x.propertyName === te.key);
      hasKey = cp ? false : true;
    }

    sqlType = hasKey ? SQLType.Update : SQLType.Insert;
    keyField = hasKey ? getSQLInfoKey(keyVal, te.key) : undefined;
  } else {
    sqlType = SQLType.InsertOrUpdate;
  }

  // Make new ChangeTrackable to manuplate
  const changeTrackable = getChangeTrackable(te, sqlType);
  let dicMappings = te.getDbMappings();
  return getSQLInfoList2(changeTrackable, dicMappings, sqlType, keyField);
}

function SQLFieldListEquals(list1: SQLField[], list2: SQLField[]): boolean {
  return _.isEqual(list1, list2);
}

function getSQLInfoKey(keyValue: any, keyPropertyName: string = "id") {
  return new SQLField({
    propertyName: _.upperFirst(keyPropertyName),  // PascalCase
    value: keyValue
  });
}

/**
 * Make a ChangeTrackable object from trackableEntity to generate SQLInfo list
 * @param trackableObj 
 * @param sqlType 
 * @param parentName 
 */
export function getChangeTrackable(trackableObj: ChangeTrackable | null, sqlType: SQLType = SQLType.Update, parentName: string = ''): ChangeTrackable | null {
  if(trackableObj == null) return null;
  const ct:ChangeTrackable = { 
    key: trackableObj.key,
    changedProps: [],
    complexChangedProps: [],
    changedCollectionProps: [],
    isDirty: trackableObj.isDirty,
    trackable: trackableObj.trackable,
    noTrackProps: trackableObj.noTrackProps,
    //<save-sqlinfo: Change as static of TrackableEntity>
    //acceptChanges: trackableObj.acceptChanges,
    //rejectChanges: trackableObj.rejectChanges
  };

  if(sqlType === SQLType.Insert) {
    // Apply all props in dicFieldMappins to changedProps, complexChangedProps, changedCollectionProps
    checkPropertyChangesOfAddedItem(trackableObj, parentName);
  }

  // Adjust property name path, getdate
  trackableObj.changedCollectionProps?.forEach(x => {
    let collPropName = `${parentName ? parentName + '.' : ''}${x.collectionName}`;
    const ccProp = { 
      collectionName: x.collectionName,
      addedItems: [],
      changedItems: [],
      deletedItems: []
     } as CollectionChangedProperty;
    ct.changedCollectionProps.push(ccProp);

    // Added
    x.addedItems.forEach(o => {
      if(!o.keyPropertyName) return;
      let oc = _.cloneDeep(o);
      oc.keyPropertyName = `${collPropName}.${o.keyPropertyName}`;
      
      // Make changeProps for addItem
      checkPropertyChangesOfAddedItem(o.changeTrackable as ChangeTrackable, collPropName);
      oc.changeTrackable = getChangeTrackable(o.changeTrackable, sqlType, collPropName);
      ccProp.addedItems.push(oc);
    });

    // Changed
    x.changedItems.forEach(o => {
      if(!o.keyPropertyName) return;
      let oc = _.cloneDeep(o);
      oc.keyPropertyName = `${collPropName}.${o.keyPropertyName}`;
      oc.changeTrackable = getChangeTrackable(o.changeTrackable, sqlType, collPropName);
      ccProp.changedItems.push(oc);
    });

    // Deleted
    x.deletedItems.forEach(o => {
      let oc = _.cloneDeep(o);
      oc.keyPropertyName = `${collPropName}.${o.keyPropertyName}`;
      oc.changeTrackable = getChangeTrackable(o.changeTrackable, sqlType, collPropName);
      ccProp.deletedItems.push(oc);
    });

  });

  trackableObj.complexChangedProps?.forEach(x => {
    let propName = `${parentName ? parentName + '.' : ''}${x.complexName}`;
    const ccProp: ComplexChangedProperty = {
      complexName: propName,
      complexItem: null
    };

    const cci = { 
      keyPropertyName: undefined, 
      keyValue: undefined, 
      changeTrackable: null 
    } as CollectionChangedItem;
    
    if(x.complexItem === null) return;
    cci.changeTrackable = getChangeTrackable(x.complexItem.changeTrackable, sqlType, propName);
    if(!x.complexItem.keyPropertyName && cci.changeTrackable !== null) {
      // With no key defined, add to parent changedProps/changedCollectionProps
      if(cci.changeTrackable.changedProps.length > 0)
        ct.changedProps.push(...cci.changeTrackable.changedProps);
      if(cci.changeTrackable.changedCollectionProps.length > 0)
        ct.changedCollectionProps.push(...cci.changeTrackable.changedCollectionProps);
    } else {
      cci.keyPropertyName = `${propName}.${x.complexItem.keyPropertyName}`;
      cci.keyValue = x.complexItem.keyValue;
      ccProp.complexItem = cci;
      ct.complexChangedProps.push(ccProp);
    }
  });

  trackableObj.changedProps?.forEach(x => {
    let propPath = `${parentName ? parentName + '.' : ''}${x.propertyName}`;
    // const cp = { propertyName: propName, oldValue: x.oldValue, newValue: x.newValue} as ChangedProperty;
    // if((trackableObj as TrackableEntity).getdateProps.indexOf(x.propertyName) >= 0)
    //   cp.newValue = "GetDate()";
    
    // ct.changedProps.push(cp);
    ct.changedProps.push(createChangedProperty(trackableObj, propPath, x.oldValue, x.newValue));
  });
  return ct;
}

function createChangedProperty(trackableObj: ChangeTrackable, propPath:string, oldValue?:any, newValue?:any):ChangedProperty {
  const lastdot = propPath.lastIndexOf('.');
  let propName = //`${parentName ? parentName + '.' : ''}${propertyName}`;
    //propertyName;
     lastdot < 0 ? propPath : propPath.substring(lastdot + 1);
  
  // if(propertyName === 'crDbDetail.revenue') {
  //   debugger;
  // }
  const cp = { 
    propertyName: propPath,   // propName, 
    oldValue: oldValue || getOriginalValue(trackableObj as TrackableEntity, propName), 
    newValue: newValue || trackableObj[propName]    // caution: 0 || undefined -> undefined, undefined || 0 -> 0
  } as ChangedProperty;

  if((trackableObj as TrackableEntity).getdateProps.indexOf(propName) >= 0) {
    cp.newValue = "GetDate()";
  } else {
    // keep null, but undefined to ''
    if(cp.oldValue === undefined) cp.oldValue = '';
    if(cp.newValue === undefined) cp.newValue = '';
  }
  return cp;
}

/**
 * Called only for addItem in changedCollectionProps
 * @param ctObj 
 * @returns 
 */
function checkPropertyChangesOfAddedItem(ctObj: ChangeTrackable, parentName:string) {
  if(ctObj === null) return;  // Nothing to do
  //const newObj = _.omitBy(_.omit(ctObj, ctObj.noTrackProps??[]), _.isFunction);
  const dicMap = (ctObj as TrackableEntity).getDbMappings(parentName);
  const propSet = new Set();
  // Treat dotted key as one parent key name
  _.keys(dicMap).forEach(k => {
    if(k === ctObj.key) return;
    const dotIndex = k.indexOf('.');
    propSet.add(dotIndex > 0 ? k.substring(0, dotIndex) : k);
  });

  //_.forOwn(newObj, function(val, key) {
  propSet.forEach(value => {
    const key = value as string;
    const val = ctObj[key]

    if(Array.isArray(val)) {
      // Collection
      if(val.length <= 0) return;
      if(ctObj.changedCollectionProps.find(ccp => ccp.collectionName === key)) return;

      // ApHd.changedCollectonProps
      const ccProps:CollectionChangedProperty = {
        collectionName: key,
        addedItems: [],
        changedItems:[],
        deletedItems:[]
      };
      ctObj.changedCollectionProps.push(ccProps);

      // ApHd.billDateil.forEach
      val.forEach(x => {
        ccProps.addedItems.push({
          keyPropertyName: `${key}.${x[key]}`,  // billDetail.id
          keyValue: x[x[key]],                  // x[id]
          changeTrackable: x                    // apDetail
        });  
      });

      ccProps.addedItems.forEach(x => {
        // Recursive call
        if(x.changeTrackable !== null) {
          checkPropertyChangesOfAddedItem(x.changeTrackable, key);
        }
      });
    }
    else if(val !== null && typeof val === 'object') {
      // Complex props
      checkPropertyChangesOfAddedItem(val, key);
    } else {
      // simple props
      //const keyPath = parentName ? `${parentName}.${key}` : key;
      if(!ctObj.changedProps.find(x => x.propertyName === key)) {
        ctObj.changedProps.push({
          propertyName: key,    //keyPath,
          oldValue: typeof val === 'number' ? 0 : '',
          newValue: val
        });
      }
    }
  });
}

// function getInitialValue(val: any): any {
//   switch(typeof val) {
//     case 'number': return 0;
//     case 'string': return '';
//     default: return null;
//   }
// }

export function checkPropDirty(trackableObj:TrackableEntity, propName:string):boolean {
  return trackableObj.changedProps.findIndex(x => x.propertyName === propName) >= 0;
}

export function getOriginalValue(trackableObj:TrackableEntity, propName:string):any {
  const index = trackableObj.changedProps.findIndex(x => x.propertyName === propName);
  if(index >= 0)
    return trackableObj.changedProps[index].oldValue;
  else
    return (trackableObj as IDictionary<any>)[propName];
}